import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Alert, Badge, Button, Card, Checkbox, Field, Select, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { moduleName } from '../access/data';
import { KEYS, type Setting } from './data';

/** Parámetros generales, agrupados por módulo. Cada uno se guarda o se restablece por separado. */
export function SettingsSection() {
  const settings = useQuery({ queryKey: KEYS.valores, queryFn: () => api<Setting[]>('/api/parametros/valores') });
  if (!settings.data) return settings.error ? <Alert>{errorMessage(settings.error)}</Alert> : <Spinner label={t('comun.cargando')} />;

  const modules = [...new Set(settings.data.map((s) => s.module))];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('param.generales')}</h2>
      {modules.map((m) => (
        <Card key={m} title={moduleName(m)}>
          <ul className="divide-y divide-slate-100">
            {settings.data
              .filter((s) => s.module === m)
              .map((s) => (
                <li key={s.key} className="py-3 first:pt-0 last:pb-0">
                  <SettingRow setting={s} />
                </li>
              ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}

function display(s: Setting, value: unknown): string {
  if (s.type === 'boolean') return value ? t('param.si') : t('param.no');
  if (s.type === 'enum') return s.options.find((o) => o.value === value)?.label ?? String(value);
  if (s.type === 'integer' && s.unit) return `${String(value)} ${s.unit}`;
  return String(value);
}

function SettingRow({ setting: s }: { setting: Setting }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<unknown>(s.value);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = `param-${s.key.replace(/\W/g, '-')}`;
  const dirty = draft !== s.value;

  async function act(fn: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: KEYS.valores });
      setNotice(message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const save = (e: FormEvent) => {
    e.preventDefault();
    void act(() => api(`/api/parametros/valores/${s.key}`, { method: 'PUT', json: { value: draft } }), t('param.guardado'));
  };
  const reset = () =>
    void act(async () => {
      await api(`/api/parametros/valores/${s.key}`, { method: 'DELETE' });
      setDraft(s.default);
    }, t('param.restablecido'));

  let input;
  switch (s.type) {
    case 'integer':
      input = (
        <Field
          id={id}
          label={s.unit ? `${s.label} (${s.unit})` : s.label}
          type="number"
          min={s.min}
          max={s.max}
          step={1}
          required
          value={String(draft)}
          onChange={(e) => setDraft(e.target.value === '' ? '' : Number(e.target.value))}
        />
      );
      break;
    case 'boolean':
      input = <Checkbox id={id} label={<span className="font-medium">{s.label}</span>} checked={draft === true} onChange={(e) => setDraft(e.target.checked)} />;
      break;
    case 'time':
      input = <Field id={id} label={s.label} type="time" required value={String(draft)} onChange={(e) => setDraft(e.target.value)} />;
      break;
    case 'text':
      input = <Field id={id} label={s.label} maxLength={s.maxLength} value={String(draft)} onChange={(e) => setDraft(e.target.value)} />;
      break;
    case 'enum':
      input = (
        <Select id={id} label={s.label} value={String(draft)} onChange={(e) => setDraft(e.target.value)}>
          {s.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );
      break;
  }

  return (
    <form onSubmit={save} className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <div className={s.type === 'boolean' ? 'flex-1' : 'w-full max-w-xs'}>{input}</div>
        <Button type="submit" className="px-3 py-1.5" disabled={busy || !dirty || draft === ''}>
          {t('comun.guardar')}
        </Button>
        {!s.isDefault && (
          <Button type="button" variant="secondary" className="px-3 py-1.5" disabled={busy} onClick={reset}>
            {t('param.restablecer')}
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500">{s.description}</p>
      <p className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span>{t('param.por_defecto', { valor: display(s, s.default) })}</span>
        {!s.isDefault && s.updatedAt && (
          <Badge tone="blue">{t('param.modificado_por', { nombre: s.updatedBy ?? '—', fecha: new Date(s.updatedAt).toLocaleDateString('es-PA') })}</Badge>
        )}
      </p>
      {error && <Alert>{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
    </form>
  );
}
